import { useState } from 'react';
import { API_BASE } from '../../lib/api';
import { Icon } from '../../components/HeartMark';
import { Card } from '../../components/Bits';

/**
 * What to do with the token a link just handed back, for the one provider that is the patient's
 * own phone.
 *
 * It is shown ONCE. Only its hash was stored, so there is no screen anywhere that can show it
 * again — and this page says so rather than letting someone discover it later. Losing it is not a
 * disaster: disconnect and connect again, which mints a new one and makes the old one resolve to
 * nobody.
 *
 * The steps are written for the Shortcuts app as a person actually meets it. No HMAC, no OAuth, no
 * developer account: a dictionary and a POST, on a daily automation.
 */
export function AppleSetup({ secret, onDone }: { secret: string; onDone: () => void }) {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (what: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(what);
      setTimeout(() => setCopied(null), 1600);
    } catch {
      setCopied('Copy did not work — select the text and copy it by hand.');
    }
  };

  // From the API base the app already talks to, never from `location.origin`. Inside the native
  // shell the origin is `capacitor://localhost`, and deriving the address from it printed a URL
  // that went nowhere — the one address on this page a patient has to type correctly.
  const endpoint = `${API_BASE.startsWith('http') ? API_BASE : location.origin.replace('//heartcoach.', '//heartcoach-api.')}/ingest/apple/webhook`;

  return (
    <Card>
      <div className="card-head">
        <h2>Finish on your iPhone</h2>
        <Icon name="watch" size={18} />
      </div>

      <p className="hint">
        Apple does not let any website read Health data — only an app on your phone can. So your phone sends it to us, on a
        schedule you choose, using Apple&apos;s own Shortcuts app. Nothing to install.
      </p>

      <div className="callout">
        <strong>Your device token — copy it now</strong>
        <p className="hint">This is the only time it is shown. We keep only a fingerprint of it, so we cannot show it again. If you lose it, disconnect and connect again.</p>
        <code className="token">{secret}</code>
        <button type="button" className="button secondary small" onClick={() => void copy('token', secret)}>
          {copied === 'token' ? 'Copied' : 'Copy token'}
        </button>
      </div>

      <ol className="steps">
        <li>
          Open <strong>Shortcuts</strong> on your iPhone, go to <strong>Automation</strong>, and add a new one for a
          <strong> Time of Day</strong> — bedtime works well. Turn off &quot;Ask Before Running&quot;.
        </li>
        <li>
          Add <strong>Find Health Samples</strong>. Choose what you want to send — Steps, Resting Heart Rate, Body Mass,
          Sleep — and set it to today. Add one action per number you want.
        </li>
        <li>
          Add <strong>Get Contents of URL</strong>, set Method to <strong>POST</strong>, and paste this address:
          <code className="token">{endpoint}</code>
          <button type="button" className="button secondary small" onClick={() => void copy('url', endpoint)}>
            {copied === 'url' ? 'Copied' : 'Copy address'}
          </button>
        </li>
        <li>
          Under <strong>Request Body</strong> choose <strong>JSON</strong> and add a text field called <code>token</code>
          with your token above. Then add a number field for each thing you are sending, named exactly:
          <ul className="plain hint">
            <li><code>steps</code> · <code>restingHeartRate</code> · <code>weightLbs</code> · <code>sleepHours</code></li>
            <li><code>exerciseMinutes</code> · <code>hrv</code></li>
            <li><code>systolic</code> and <code>diastolic</code> together, if a cuff writes to Health</li>
          </ul>
          Leave out anything you do not want to send. Everything is optional except the token.
        </li>
        <li>Run it once by hand. Your numbers should appear on the Health tab within a minute.</li>
      </ol>

      <p className="hint">
        What this sends is the numbers you chose and nothing else — no location, no messages, nothing else on your phone.
        Numbers that arrive this way fill in your record; they earn no HeartCoins, and they never raise an alert on their
        own. Coins are for what you decide to do.
      </p>

      <button type="button" className="button primary" onClick={onDone}>
        Done
      </button>
    </Card>
  );
}
